import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  Loader,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Notification,
  NotificationType,
  TextTooltip,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";
import { Button, ToggleButton } from "@transfr-inc/dashboard-components/forms";
import {
  formatDateDashes,
  getUTCDate,
} from "@transfr-inc/dashboard-components/utils";

import {
  getCareerOptions,
  getProductsOptions,
  getProgramsOptions,
  programKey,
  getSimulationsOptions,
  IntegrationIdp,
  OrgDetailsEditor,
  OrgExpirationDateEditor,
  OrgFormEditor,
  OrgFormToggle,
  OrgIntegrationExtIdEditor,
  OrgProductsEditor,
  OrgProgramsEditor,
  OrgSelectMenuEditor,
  OrgSelectParentEditor,
  OrgSimsEditor,
  OrgTraineeLimitEditor,
  OrgTrekCareersEditor,
  OrgTrekLicensesEditor,
} from "@components/forms/org-editor";
import { MULTI_ORG_PARENT } from "@constants/organization-types";
import { trackProgramsAssigned } from "@services/xapi-tracking";
import { ApplicationId } from "@lib/constants";
import { ConfigurationKeys } from "@services/configuration";
import { useConfigurations } from "@hooks";
import { OrganizationContext } from "@src/context";
import { services } from "@src/dependencies";
import { useApiRequest } from "@utils/http-client";

import "./edit-org.modal.scss";

export default ({
  open,
  onClose,
  organization,
  onUpdateOrg,
  onParentChange,
}) => {
  if (!open) {
    return null;
  }
  const [areCareersLoaded, setAreCareersLoaded] = useState();
  const { updateOrganizations } = useContext(OrganizationContext);
  const [loading, setLoading] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [showTSMenu, setShowTSMenu] = useState();
  const [showSims, setShowSims] = useState();
  const [showTrek, setShowTrek] = useState();
  const [showCareers, setShowCareers] = useState();
  const [showPrograms, setShowPrograms] = useState();
  const [hasPopulatedForm, setHasPopulatedForm] = useState(false);
  const methods = useForm({
    criteriaMode: "all",
    mode: "onChange",
  });
  const { reset, handleSubmit, formState, watch } = methods;
  const {
    organizationService,
    menuBuilderService,
    careersService,
    trekService,
    programsService,
    configurationService,
  } = services;

  const isMultiOrgParent = organization?.typeId === MULTI_ORG_PARENT.id;
  const [deviceLastOnlineEnabled, setDeviceLastOnlineEnabled] = useState(false);
  const [initialDeviceLastOnline, setInitialDeviceLastOnline] = useState(false);
  const [deviceLastOnlineParam, setDeviceLastOnlineParam] = useState();
  const [deviceConfigWhitelistedUrls, setDeviceConfigWhitelistedUrls] =
    useState([]);

  const { loading: loadingDeviceConfig, getConfigurations: loadDeviceConfig } =
    useConfigurations(ApplicationId.DASHBOARD, organization?.id, {
      onSuccess: (configs) => {
        const param = configs[ConfigurationKeys.DEVICE_LAST_ONLINE_ENABLED];
        if (!param) return;
        setDeviceLastOnlineParam(param);
        setDeviceConfigWhitelistedUrls(configs.whitelistedUrls ?? []);
        const val = param.value === "true";
        setDeviceLastOnlineEnabled(val);
        setInitialDeviceLastOnline(val);
      },
    });
  const productsWatch = watch("products");
  const integrationsWatch = watch("integrations");
  const orgIdpWatch = watch("orgIdp");
  const integrationExternalIdWatch = watch("integrationExternalId");
  const showRosterSync =
    integrationsWatch && !!orgIdpWatch?.id && !!integrationExternalIdWatch;

  const { response: parentOrgs = [], sendRequest: getParentOrgs } =
    useApiRequest(() => organizationService.getParentOrgs(), false);

  const { response: idpList = [], sendRequest: getIdpList } = useApiRequest(
    () => organizationService.getIdpList(),
    false,
  );

  const {
    response: orgMenu,
    resetResponse: resetOrgMenu,
    sendRequest: getOrgMenu,
    loading: loadingOrgMenu,
  } = useApiRequest(
    () => organizationService.getOrganizationMenu(organization?.code),
    false,
  );

  const {
    response: tsMenus,
    sendRequest: getTSMenus,
    loading: loadingTSMenus,
  } = useApiRequest(() => menuBuilderService.getAllMenus(), false);

  const {
    response: orgTrekCareers,
    resetResponse: resetOrgTrekCareers,
    sendRequest: getOrgTrekCareers,
    loading: loadingOrgTrekCareers,
  } = useApiRequest(() => trekService.getCareers(organization?.code), false);

  const {
    response: trekCareers,
    sendRequest: getTrekCareers,
    loading: loadingTrekCareers,
  } = useApiRequest(() => trekService.getCareers(), false);

  const {
    response: orgSimulations,
    resetResponse: resetOrgSimulations,
    sendRequest: getOrgSimulations,
    loading: loadingOrgSimulations,
  } = useApiRequest(
    () => organizationService.getOrganizationSimulations(organization?.code),
    false,
  );

  const {
    response: sims = [],
    sendRequest: getSimulations,
    loading: loadingSimulations,
  } = useApiRequest(() => careersService.getAllSimulations(), false);

  const {
    response: orgPrograms,
    resetResponse: resetOrgPrograms,
    sendRequest: getOrgPrograms,
    loading: loadingOrgPrograms,
  } = useApiRequest(
    () => programsService.getCoursesByOrgCode(organization?.code),
    false,
  );

  const {
    response: allPrograms = [],
    sendRequest: getPrograms,
    loading: loadingPrograms,
  } = useApiRequest(() => programsService.getAllPrograms(), false);

  const loadTrekCareers = async () => {
    setAreCareersLoaded(false);

    await Promise.allSettled([getTrekCareers(), getOrgTrekCareers()]);
    setAreCareersLoaded(true);
  };

  const onConfirmChanges = () => {
    setLoading(true);
    setErrorMessage();

    handleSubmit(async (values) => {
      const dataUpdated = getUpdateData(values);
      try {
        const updatedOrg =
          await organizationService.updateOrgMetadata(dataUpdated);

        if (values.products.map((p) => p.id).includes(Product.TRK)) {
          await trekService.upsertOrganizationCareers(organization.code, {
            hasAllCareers: values.allCareers,
            careerIds: values.careers ? values.careers.map((p) => p.id) : [],
          });
        }

        if (
          !isMultiOrgParent &&
          deviceLastOnlineEnabled !== initialDeviceLastOnline &&
          deviceLastOnlineParam
        ) {
          await configurationService.updateOrganizationConfiguration(
            ApplicationId.DASHBOARD,
            organization.id,
            {
              config_settings: [
                {
                  ...deviceLastOnlineParam,
                  value: String(deviceLastOnlineEnabled),
                },
              ],
              whitelisted_urls: deviceConfigWhitelistedUrls,
            },
          );
          setInitialDeviceLastOnline(deviceLastOnlineEnabled);
        }

        updateOrganizations();
        onUpdateOrg && onUpdateOrg(updatedOrg);
        if (onParentChange) {
          onParentChange(values.parent);
        }
        if (values.programs) {
          const oldProgramIds = orgPrograms?.map((p) => programKey(p)) || [];

          const addedPrograms = values.programs.filter(
            (program) => !oldProgramIds.includes(program.id),
          );

          if (addedPrograms.length > 0) {
            await trackProgramsAssigned(addedPrograms, updatedOrg?.id);
          }
        }
      } catch (error) {
        const message =
          error.data?.detail ?? "Something went wrong. Please try again.";
        setErrorMessage(message);
        console.error(
          "[Edit Org] - Something went wrong. Error Details >>>",
          error,
        );
      } finally {
        setLoading();
      }
    })();
  };

  const getUpdateData = (data) => {
    const orgUpdated = { code: organization.code, name: data.name };

    if (organization.typeId !== MULTI_ORG_PARENT.id) {
      orgUpdated.traineeLimit =
        data.seats >= 0 && Number.parseInt(data.seats || 0);
      orgUpdated.trekLicenses =
        data.trekLicenses >= 0 && Number.parseInt(data.trekLicenses || 0);
      orgUpdated.instructorSeats = organization.instructorSeats;
      orgUpdated.expiration =
        data.expiration && formatDateDashes(data.expiration);
      orgUpdated.children = data?.children && data.children.map((c) => c.id);
      orgUpdated.parent = data.parent?.id;
      orgUpdated.products = data.products && data.products.map((p) => p.id);
      orgUpdated.menu = data.menu && data.menu.id;
      orgUpdated.allSims = data.allSims;
      orgUpdated.simulations = data?.simulations
        ? data.simulations.map((s) => s.id)
        : [];
      orgUpdated.integrations = data.integrations;
      if (data.integrationExternalId) {
        orgUpdated.externalOrgCodes = [data.integrationExternalId];
      }
      orgUpdated.ssoEnabled = data.ssoEnabled;
      orgUpdated.rosterSyncEnabled = data.rosterSyncEnabled;
      orgUpdated.orgIdpId = data.orgIdp?.id;
      orgUpdated.program_ids = data?.programs
        ? data.programs.map((p) => p.id)
        : [];
    }

    return orgUpdated;
  };

  const resetForm = () => {
    reset({
      ...organization,
      expiration: organization.expiration
        ? getUTCDate(organization.expiration)
        : undefined,
      products: getProductsOptions(organization.products),
      parent: organization.parent,
      menu: orgMenu,
      simulations: getSimulationsOptions(orgSimulations?.simulations),
      allSims: orgSimulations?.allSims,
      careers: getCareerOptions(orgTrekCareers),
      allCareers: trekCareers?.length == orgTrekCareers?.length,
      programs: getProgramsOptions(orgPrograms),
      integrations: organization.integrations,
      integrationExternalId: organization.orgExternalIds?.length
        ? organization.orgExternalIds[0]
        : "",
      ssoEnabled: organization.ssoEnabled,
      rosterSyncEnabled: organization.rosterSyncEnabled,
      orgIdp: organization.orgIdp,
    });

    if (organization?.typeId !== MULTI_ORG_PARENT.id) {
      getParentOrgs();
      getTSMenus();
      getSimulations();
      getIdpList();
      getPrograms();
    }
  };

  const resetOrganizationCustomData = () => {
    resetOrgMenu();
    resetOrgTrekCareers();
    resetOrgSimulations();
    resetOrgPrograms();
  };

  useEffect(() => {
    if (!organization?.code) {
      return;
    }

    setHasPopulatedForm(false);

    if (organization?.typeId === MULTI_ORG_PARENT.id) {
      resetForm();
      setHasPopulatedForm(true);
      return;
    }

    setLoading(true);
    getOrgMenu();
    getOrgSimulations();
    getOrgPrograms();
    loadDeviceConfig();
    // To indicate that an organization has all the Trek careers or a subset of them,
    // we need both responses to display the correct view.
    loadTrekCareers();
  }, [organization?.code, organization?.typeId]);

  useEffect(() => {
    if (
      hasPopulatedForm ||
      organization?.typeId === MULTI_ORG_PARENT.id ||
      !organization?.code
    ) {
      return;
    }

    const hasLoadedOrgData =
      !loadingOrgMenu &&
      !loadingTSMenus &&
      !loadingTrekCareers &&
      !loadingSimulations &&
      !loadingPrograms &&
      !loadingOrgSimulations &&
      !loadingOrgPrograms &&
      areCareersLoaded === true;

    if (!hasLoadedOrgData) {
      return;
    }

    setLoading();
    resetForm();
    setHasPopulatedForm(true);
  }, [
    areCareersLoaded,
    hasPopulatedForm,
    loadingOrgMenu,
    loadingTSMenus,
    loadingTrekCareers,
    loadingSimulations,
    loadingOrgPrograms,
    loadingPrograms,
    loadingOrgSimulations,
    organization?.code,
    organization?.typeId,
  ]);

  useEffect(() => {
    setShowTSMenu(productsWatch?.find((p) => p.id === Product.TS));
    setShowSims(productsWatch?.find((p) => p.id === Product.CE));
    setShowTrek(productsWatch?.find((p) => p.id === Product.TRK));
    setShowCareers(productsWatch?.find((p) => p.id === Product.TRK));
    setShowPrograms(productsWatch?.find((p) => p.id === Product.PROG));
  }, [productsWatch]);

  useEffect(() => {
    reset({});
    // When closing the modal, it doesn't reset the custom data from the organization
    // because the modal is being hidden instead of unmounted.
    // This can cause confusion when opening the modal with a new organization and previous data loaded.
    resetOrganizationCustomData();
    setHasPopulatedForm(false);
  }, [open]);

  const allDataLoaded = useMemo(() => {
    return (
      !loadingOrgMenu &&
      !loadingOrgTrekCareers &&
      !loadingTrekCareers &&
      !loadingOrgSimulations &&
      !loadingSimulations &&
      !loadingOrgPrograms &&
      !loadingPrograms &&
      !loadingTSMenus &&
      areCareersLoaded !== false
    );
  }, [
    loadingOrgMenu,
    loadingOrgTrekCareers,
    loadingTrekCareers,
    loadingOrgSimulations,
    loadingSimulations,
    loadingOrgPrograms,
    loadingPrograms,
    areCareersLoaded,
    loadingTSMenus,
  ]);

  return (
    <Modal
      modalClassName={"edit-org-modal"}
      open={open}
      onClose={onClose}
      uniqueName="edit-org-modal"
    >
      {loading && <Loader overlay />}
      <ModalHeader
        className="blue-icon small"
        icon={["fa-regular", "edit"]}
        title="Edit Organization"
      />

      <ModalBody className="modal-org-details">
        <FormProvider {...methods}>
          <OrgFormEditor>
            {errorMessage && (
              <Notification
                animated
                closable
                onClose={() => setErrorMessage()}
                type={NotificationType.error}
              >
                {errorMessage}
              </Notification>
            )}
            <OrgDetailsEditor />
            {organization?.typeId !== MULTI_ORG_PARENT.id && (
              <>
                <OrgProductsEditor
                  placeholder={"Select one or more products..."}
                />
                {showTSMenu && (
                  <div>
                    <OrgSelectMenuEditor
                      menus={tsMenus}
                      isEdit
                      loadingMenus={loadingTSMenus}
                    />
                  </div>
                )}
                {showSims && (
                  <div>
                    {loadingSimulations ? (
                      <Loader overlay />
                    ) : (
                      <OrgSimsEditor required data={sims} />
                    )}
                  </div>
                )}
                {showCareers && (
                  <div>
                    {loadingTrekCareers ? (
                      <Loader overlay />
                    ) : (
                      <OrgTrekCareersEditor required data={trekCareers} />
                    )}
                  </div>
                )}
                {showPrograms && (
                  <div>
                    {loadingPrograms ? (
                      <Loader overlay />
                    ) : (
                      <OrgProgramsEditor required data={allPrograms} />
                    )}
                  </div>
                )}
                <OrgSelectParentEditor orgs={parentOrgs} clearEnabled />
                <IntegrationIdp idpList={idpList} />
                <OrgIntegrationExtIdEditor />
                <OrgFormToggle
                  className="edit-org-integrations"
                  name="integrations"
                  title="Integrations"
                  infoText="Would you like to enable external integrations?"
                />
                {showRosterSync && (
                  <OrgFormToggle
                    name="rosterSyncEnabled"
                    title="Enable Roster Sync"
                    infoText="Enable synchronization of rostering data"
                  />
                )}
                <OrgFormToggle
                  name="ssoEnabled"
                  title="Enable Single Sign-On"
                  infoText="Enable sign on with your organization credentials."
                />
                <div className="toggle-input">
                  <h4>Last Active Device</h4>
                  <TextTooltip
                    className="info"
                    text="Show the Last Active column on the Devices tab for this organization."
                  >
                    <FontAwesomeIcon icon={["fa-light", "fa-circle-info"]} />
                  </TextTooltip>
                  <ToggleButton
                    className="toggle-button"
                    onChange={setDeviceLastOnlineEnabled}
                    value={deviceLastOnlineEnabled}
                    affirmText="YES"
                    negateText="NO"
                  />
                </div>
                <OrgTraineeLimitEditor />
                {showTrek && <OrgTrekLicensesEditor name="trekLicenses" />}
                <OrgExpirationDateEditor />
              </>
            )}
          </OrgFormEditor>
        </FormProvider>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          primary
          onClick={onConfirmChanges}
          disabled={!formState.isValid || !allDataLoaded || loading}
        >
          Apply
        </Button>
      </ModalFooter>
    </Modal>
  );
};
